import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  CustomerSelect,
  CompanySelect,
  FormatDateToMDY,
  FormatValueToDecimal,
  DateSelect,
  BOMItemSelect
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import { PDFDocument  } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function ProductionReport() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Customer 
  const [customer, setCustomer] = useState(null);
  const [item, setItem] = useState(null);
  const [company, setCompany] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);

  const openCustomerSelect = () => {
    setShowCustomerSelect(true)
  }
  const closeCustomerSelect = () => {
    setShowCustomerSelect(false)
  }

  const [showItemSelectForm, setShowItemSelectForm] = useState(false);
  const openItemSelectForm = () => {
    setShowItemSelectForm(true);
  }
  const closeItemSelectForm = () => {
    setShowItemSelectForm(false);
  }
//#endregion







//#region Generate
  const fetchReportData = useCallback(async () => {
    const body = {
      customerId: customer?.customerId || null,
      dateFrom: dateFrom,
      dateTo: dateTo,
      itemCode: item?.itemCode || null
    }

    try {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/production-report`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [dateFrom, dateTo, customer, item]);

  const generateHandleClick = async () => {
    if (!company || !dateFrom || !dateTo) {
      setFailMessage("Please fill up required fields");
      return;
    }

    setLoading(true);

    try {
      const _data = await fetchReportData();

      if (!_data || _data.length === 0) {
        setFailMessage("No data found for the selected filter");
        setLoading(false);
        return;
      }

      // --- TEMPLATE LAYOUT COORDINATES ---
      // const TABLE_LEFT = 35;
      // const TABLE_RIGHT = 540;

      const COL_X_DATE = 40;
      const COL_X_CUSTOMER = 100;
      const COL_X_ITEM = 210;
      const COL_X_TOTAL_VOLUME = 340;
      const COL_X_G1 = 398;
      const COL_X_34 = 440;
      const COL_X_38 = 490;
      const COL_X_WASHED_SAND = 543;
      const COL_X_CEMENT = 600;
      const COL_X_PSP_PX = 655;
      const COL_X_PSP_PR = 710;
      const PAGE_NO_X = 710;              // Position next to "Page:"

      const TABLE_START_Y = 441;          // Starting Y for table rows below header lines
      const ROW_HEIGHT = 13;              // Height between rows
      const BOTTOM_THRESHOLD = 80;        // Space to trigger new page

      // --- LOAD TEMPLATE AND FONTS ---
      const pdfUrl = `${window.location.origin}/docs/PRODUCTION_REPORT.pdf`;
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();

      // Keep a clean copy of the template to duplicate pages if data exceeds Page 1
      const templateDoc = await PDFDocument.load(pdfBytes);
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontBytes = await (await fetch("/calibri.ttf")).arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);
      const boldBytes = await (await fetch("/calibrib.ttf")).arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      // --- REUSABLE HEADER FUNCTION ---
      const drawHeader = (targetPage) => {
        // Retained Company details
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 556, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 546, size: 8, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 536, size: 8, font: calibriFont });

        // Template pre-printed label coordinates
        targetPage.drawText(`${FormatDateToMDY(dateFrom)} to ${FormatDateToMDY(dateTo)}`, { x: 85, y: 506, size: 8, font: calibriFont });
      };

      let currentPage = pdfDoc.getPages()[0];
      drawHeader(currentPage);

      let yPosition = TABLE_START_Y;

      // --- DRAW DATA ROWS ---
      for (let i = 0; i < _data.length; i++) {
        const item = _data[i];

        // Trigger new page if reaching the bottom
        if (yPosition < BOTTOM_THRESHOLD) {
          const [newPage] = await pdfDoc.copyPages(templateDoc, [0]);
          pdfDoc.addPage(newPage);
          currentPage = newPage;
          drawHeader(currentPage);
          yPosition = TABLE_START_Y;
        }

        const dateStr = item.productionDate ? FormatDateToMDY(item.productionDate ) : "";
        let customerStr = (item.customerName1 || "")?.toString();
        let itemStr = (item.itemDescription || "")?.toString();

        // Truncate customer name if long to prevent collision with Amount column
        const maxCustWidth = 220;
        if (calibriFont.widthOfTextAtSize(customerStr, 8) > maxCustWidth) {
          while (customerStr.length > 0 && calibriFont.widthOfTextAtSize(customerStr + "...", 8) > maxCustWidth) {
            customerStr = customerStr.slice(0, -1);
          }
          customerStr += "...";
        }

        // 1. Date
        currentPage.drawText(dateStr, {
          x: COL_X_DATE,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        // 2. Customer
        currentPage.drawText(customerStr, {
          x: COL_X_CUSTOMER,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        // 3. Item
        currentPage.drawText(itemStr, {
          x: COL_X_ITEM,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        // 3. Total Volume
        currentPage.drawText(`${FormatValueToDecimal(item.totalVolume)}`, {
          x: COL_X_TOTAL_VOLUME,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(`${FormatValueToDecimal(item.itemsG1)}`, {
          x: COL_X_G1,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(`${FormatValueToDecimal(item.items34)}`, {
          x: COL_X_34,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(`${FormatValueToDecimal(item.items38)}`, {
          x: COL_X_38,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(`${FormatValueToDecimal(item.itemsWashedSand)}`, {
          x: COL_X_WASHED_SAND,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(`${FormatValueToDecimal(item.itemsCement)}`, {
          x: COL_X_CEMENT,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText("", {
          x: COL_X_PSP_PX,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        currentPage.drawText(`${FormatValueToDecimal(item.itemsAdmix)}`, {
          x: COL_X_PSP_PR,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        yPosition -= ROW_HEIGHT;
      }

      // --- DRAW TOTALS SECTION ---
      // Ensure enough vertical space (~80pt) for the totals block
      if (yPosition < 95) {
        const [newPage] = await pdfDoc.copyPages(templateDoc, [0]);
        pdfDoc.addPage(newPage);
        currentPage = newPage;
        drawHeader(currentPage);
        yPosition = TABLE_START_Y;
      }

      // --- DRAW PAGE NUMBERS ---
      const allPages = pdfDoc.getPages();
      for (let i = 0; i < allPages.length; i++) {
        allPages[i].drawText(`Page: ${i + 1}`, {
          x: PAGE_NO_X,
          y: 506,
          size: 8,
          font: calibriFont,
        });
      }

      // --- SAVE AND DOWNLOAD ---
      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `PRODUCTION_REPORT.pdf`;
      a.click();
    } catch (error) {
      console.error("Error generating PDF:", error);
      setFailMessage("Error processing PDF");
    }

    setLoading(false);
  };
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasProductionReport) {
    return <NoAccess />;
  }
//#endregion





//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`Production Report`}
            </h2>
            <p className="text-gray-500 mt-1">Process, progress, and performance of production.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              Customer
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Customer"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={`${customer ? `${customer?.customerId} - ${customer?.name1}` : ""}`}
                readOnly
              />
              {!customer ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openCustomerSelect}>
                  <PersonSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setCustomer(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date From <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateFrom} setData={setDateFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date To <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateTo} setData={setDateTo} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              Item
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Item"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={`${item ? `${item?.itemCode} - ${item?.itemDescription} ${item?.brandName ? `(${item.brandName})` : ''}` : ""}`}
                readOnly
              />
              {!item ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openItemSelectForm}>
                  <PersonSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setItem(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>
        </div>

        <button onClick={generateHandleClick} className="mt-10 flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
          <RestartAltIcon fontSize="small" /> Generate
        </button>
      </div>

      <CustomerSelect 
        visible={showCustomerSelect} 
        onClose={closeCustomerSelect}
        modalName={"Select Customer"}
        data={customer} setData={setCustomer}
      />

      <BOMItemSelect 
        visible={showItemSelectForm} 
        onClose={closeItemSelectForm}
        modalName={"Select Item"}
        data={item} setData={setItem}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}